import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ChevronDown, Sun, Moon } from "lucide-react";
import { Container, Button } from "@/components/primitives";
import { useTheme } from "@/lib/theme";
import { useBooking } from "@/lib/booking";
import { business, services } from "@/data/site";
import { cn } from "@/lib/utils";

const nav = [
  { label: "Services", to: "/services", children: services },
  { label: "Smile Gallery", to: "/smile-gallery" },
  { label: "About", to: "/about" },
  { label: "Insurance", to: "/insurance-financing" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

const ThemeToggle = () => {
  const { theme, toggle } = useTheme();
  return (
    <button
      onClick={toggle}
      data-testid="theme-toggle"
      aria-label={`Switch to ${theme === "clean" ? "warm" : "clean"} theme`}
      title={theme === "clean" ? "Clean & Clinical — tap for Warm & Elevated" : "Warm & Elevated — tap for Clean & Clinical"}
      className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--border)] text-[var(--fg)] transition-colors hover:border-[var(--primary)] hover:text-[var(--primary)]"
    >
      {theme === "clean" ? <Sun size={17} /> : <Moon size={17} />}
    </button>
  );
};

const Logo = () => (
  <Link to="/" data-testid="logo-link" className="group flex items-center gap-2.5">
    <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--primary)] text-[var(--primary)] transition-transform duration-500 group-hover:rotate-90">
      <span className="h-2.5 w-2.5 rounded-full bg-[var(--primary)]" />
    </span>
    <span className="flex flex-col leading-none">
      <span className="font-heading text-xl tracking-tight text-[var(--fg)]">Meridian</span>
      <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[var(--muted-fg)]">Dental Studio</span>
    </span>
  </Link>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const { openBooking } = useBooking();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-all duration-500",
        scrolled ? "glass border-b border-[var(--border)] py-3" : "py-5"
      )}
    >
      <Container className="flex items-center justify-between gap-4">
        <Logo />

        <nav className="hidden items-center gap-1 lg:flex">
          {nav.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <Link
                  to={item.to}
                  data-testid="nav-services"
                  className="flex items-center gap-1 px-4 py-2 text-sm text-[var(--fg)]/80 transition-colors hover:text-[var(--primary)]"
                >
                  {item.label}
                  <ChevronDown size={14} className={cn("transition-transform", servicesOpen && "rotate-180")} />
                </Link>
                <AnimatePresence>
                  {servicesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.25 }}
                      className="absolute left-1/2 top-full w-[340px] -translate-x-1/2 pt-3"
                    >
                      <div className="glass overflow-hidden rounded-2xl border border-[var(--border)] p-2 shadow-2xl">
                        {item.children.map((s) => (
                          <Link
                            key={s.slug}
                            to={`/services/${s.slug}`}
                            data-testid={`nav-service-${s.slug}`}
                            className="flex items-start gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-[var(--surface-2)]"
                          >
                            <span className="font-mono text-xs text-[var(--primary)]">{s.index}</span>
                            <span>
                              <span className="block text-sm font-medium text-[var(--fg)]">{s.name}</span>
                              <span className="mt-0.5 block text-xs text-[var(--muted-fg)]">{s.short}</span>
                            </span>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                key={item.label}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className="px-4 py-2 text-sm text-[var(--fg)]/80 transition-colors hover:text-[var(--primary)]"
              >
                {item.label}
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-2.5">
          <ThemeToggle />
          <a
            href={business.phoneHref}
            data-testid="header-call-link"
            className="hidden items-center gap-2 rounded-full border border-[var(--border)] px-4 py-2.5 text-sm text-[var(--fg)] transition-colors hover:border-[var(--primary)] hover:text-[var(--primary)] xl:flex"
          >
            <Phone size={15} />
            {business.phoneDisplay}
          </a>
          <Button
            onClick={() => openBooking()}
            data-testid="header-book-btn"
            className="hidden sm:inline-flex"
          >
            Book Appointment
          </Button>
          <button
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-open"
            aria-label="Open menu"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--border)] text-[var(--fg)] lg:hidden"
          >
            <Menu size={20} />
          </button>
        </div>
      </Container>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] lg:hidden"
          >
            <div className="absolute inset-0 bg-[var(--bg)]" />
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              className="relative flex h-full flex-col"
            >
              <Container className="flex items-center justify-between py-5">
                <Logo />
                <button
                  onClick={() => setMobileOpen(false)}
                  data-testid="mobile-menu-close"
                  aria-label="Close menu"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--border)] text-[var(--fg)]"
                >
                  <X size={20} />
                </button>
              </Container>
              <Container className="flex flex-1 flex-col gap-1 overflow-y-auto pb-8 pt-6">
                <Link to="/" data-testid="mobile-nav-home" className="border-b border-[var(--border)] py-4 font-heading text-3xl text-[var(--fg)]">
                  Home
                </Link>
                <div className="border-b border-[var(--border)] py-4">
                  <Link to="/services" className="font-heading text-3xl text-[var(--fg)]">Services</Link>
                  <div className="mt-3 flex flex-col gap-2 pl-1">
                    {services.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        data-testid={`mobile-nav-service-${s.slug}`}
                        className="text-sm text-[var(--muted-fg)]"
                      >
                        {s.index} · {s.name}
                      </Link>
                    ))}
                  </div>
                </div>
                {nav.filter((n) => !n.children).map((item) => (
                  <Link
                    key={item.label}
                    to={item.to}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="border-b border-[var(--border)] py-4 font-heading text-3xl text-[var(--fg)]"
                  >
                    {item.label}
                  </Link>
                ))}
                <div className="mt-6 flex flex-col gap-3">
                  <Button size="lg" onClick={() => { setMobileOpen(false); openBooking(); }} data-testid="mobile-book-btn">
                    Book Appointment
                  </Button>
                  <Button size="lg" variant="outline" href={business.phoneHref} data-testid="mobile-call-btn">
                    <Phone size={16} /> Call {business.phoneDisplay}
                  </Button>
                </div>
              </Container>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
